<template>
  <div class="abr-page">
    <div class="abr-header-row">
      <h1 class="abr-title">預約總覽</h1>
      <!-- 切換顯示模式 -->
      <div class="abr-view-tabs">
        <button :class="['abr-view-tab', viewMode === 'card' && 'abr-view-tab--on']" @click="setView('card')">卡片</button>
        <button :class="['abr-view-tab', viewMode === 'calendar' && 'abr-view-tab--on']" @click="setView('calendar')">行事曆</button>
      </div>
    </div>

    <!-- 篩選列（卡片模式） -->
    <div v-if="viewMode === 'card'" class="abr-filters">
      <div class="abr-filter-group">
        <label>店家</label>
        <select v-model="filterStore" class="abr-input" @change="load">
          <option value="">全部店家</option>
          <option v-for="s in stores" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select>
      </div>
      <div class="abr-filter-group">
        <label>日期</label>
        <input type="date" v-model="filterDate" class="abr-input" @change="load" />
        <button class="abr-btn abr-btn--ghost abr-btn--sm" @click="clearDate">清除</button>
      </div>
      <div class="abr-filter-group">
        <label>狀態</label>
        <select v-model="filterStatus" class="abr-input" @change="load">
          <option value="">全部</option>
          <option value="pending_email">待客人信箱確認</option>
          <option value="pending">待確認</option>
          <option value="confirmed">已確認</option>
          <option value="completed">已完成</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>
      <button class="abr-btn abr-btn--primary" @click="load" :disabled="loading">
        <span v-if="loading" class="abr-spinner abr-spinner--sm abr-spinner--white" />
        查詢
      </button>
    </div>

    <!-- 行事曆模式：月份導覽 -->
    <div v-if="viewMode === 'calendar'" class="abr-cal-nav">
      <div class="abr-cal-nav-left">
        <button class="abr-btn abr-btn--ghost abr-btn--sm" @click="calPrev">‹</button>
        <span class="abr-cal-title">{{ calYear }} 年 {{ calMonthNum }} 月</span>
        <button class="abr-btn abr-btn--ghost abr-btn--sm" @click="calNext">›</button>
        <button class="abr-btn abr-btn--ghost abr-btn--sm" @click="calToday">今天</button>
      </div>
      <div class="abr-cal-nav-right">
        <select v-model="filterStore" class="abr-input" @change="loadCal">
          <option value="">全部店家</option>
          <option v-for="s in stores" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select>
        <select v-model="filterStatus" class="abr-input" @change="loadCal">
          <option value="">全部狀態</option>
          <option value="pending_email">待信箱確認</option>
          <option value="pending">待確認</option>
          <option value="confirmed">已確認</option>
          <option value="completed">已完成</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="abr-state">
      <span class="abr-spinner abr-spinner--lg" /> 載入中…
    </div>

    <!-- 錯誤 -->
    <div v-else-if="loadError" class="abr-alert abr-alert--error">{{ loadError }}</div>

    <!-- ───── 卡片模式 ───── -->
    <template v-else-if="viewMode === 'card'">
      <!-- 統計列 -->
      <div v-if="!loading && !loadError && reservations.length > 0" class="abr-summary">
        <span v-for="(count, status) in statusSummary" :key="status" class="abr-summary-chip" :class="`abr-summary-chip--${status}`">
          {{ STATUS_LABELS[status] }}：{{ count }}
        </span>
        <span class="abr-summary-chip abr-summary-chip--total">合計：{{ reservations.length }}</span>
      </div>
      <div v-if="reservations.length === 0" class="abr-empty">目前沒有符合條件的預約</div>
      <div v-else class="abr-list">
        <div v-for="r in reservations" :key="r.id" class="abr-card" :class="`abr-card--${r.status}`">
          <div class="abr-card__stripe" />
          <div class="abr-card__body">
            <div class="abr-card__top">
              <span class="abr-code">{{ r.reservation_code }}</span>
              <span class="abr-status-tag" :class="`abr-status-tag--${r.status}`">{{ STATUS_LABELS[r.status] }}</span>
              <span class="abr-store-badge">{{ r.store_name }}</span>
            </div>
            <div class="abr-card__meta">
              <span class="abr-method-badge" :class="r.line_user_id ? 'abr-method-badge--line' : 'abr-method-badge--email'">
                {{ r.line_user_id ? 'LINE預約' : '信箱預約' }}
              </span>
              <span>{{ r.customer_name }}</span>
              <span class="abr-sep">·</span>
              <span>{{ r.customer_phone }}</span>
              <template v-if="r.customer_email">
                <span class="abr-sep">·</span>
                <span class="abr-muted">{{ r.customer_email }}</span>
              </template>
            </div>
            <div class="abr-card__meta">
              <span>{{ r.service_name }}<span v-if="r.service_item_name"> · {{ r.service_item_name }}</span></span>
              <span class="abr-sep">·</span>
              <span>{{ r.slot_date }} {{ fmtServiceTime(r.start_time, r.duration_minutes, r.end_time) }}</span>
            </div>
            <div v-if="r.notes" class="abr-card__notes">備註：{{ r.notes }}</div>
            <div class="abr-card__created">建立：{{ fmtDatetime(r.created_at) }}</div>
            <div class="abr-card__actions">
              <template v-if="TRANSITIONS[r.status]?.length">
                <button v-for="next in TRANSITIONS[r.status]" :key="next" class="abr-btn abr-btn--sm"
                  :class="next === 'cancelled' ? 'abr-btn--cancel' : 'abr-btn--confirm'"
                  :disabled="updating[r.id]" @click="updateStatus(r, next)">
                  <span v-if="updating[r.id]" class="abr-spinner abr-spinner--sm" />
                  {{ ACTION_LABELS[next] }}
                </button>
              </template>
              <template v-else>
                <span class="abr-terminal" :class="`abr-terminal--${r.status}`">{{ STATUS_LABELS[r.status] }}</span>
                <button class="abr-btn abr-btn--sm abr-btn--delete" :disabled="deleting[r.id]" @click="deleteReservation(r)">
                  <span v-if="deleting[r.id]" class="abr-spinner abr-spinner--sm" />
                  刪除記錄
                </button>
              </template>
              <span v-if="updateErrors[r.id]" class="abr-action-err">{{ updateErrors[r.id] }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ───── 行事曆模式 ───── -->
    <template v-else-if="viewMode === 'calendar'">
      <!-- 星期標題列 -->
      <div class="cal-grid-wrap"><div class="cal-grid">
        <div class="cal-weekday" v-for="d in WEEK_DAYS" :key="d">{{ d }}</div>
        <!-- 空白前置格 -->
        <div class="cal-day cal-day--blank" v-for="i in calFirstWeekday" :key="'b'+i"></div>
        <!-- 日期格 -->
        <div
          v-for="day in calDaysInMonth"
          :key="day"
          class="cal-day"
          :class="{
            'cal-day--today': isToday(day),
            'cal-day--has': calByDate[calDateStr(day)]?.length > 0
          }"
        >
          <div class="cal-day-num">{{ day }}</div>
          <div class="cal-events">
            <div
              v-for="r in (calByDate[calDateStr(day)] || []).slice(0, 4)"
              :key="r.id"
              class="cal-event"
              :class="`cal-event--${r.status}`"
              @click="openDetail(r)"
            >
              <span class="cal-event-time">{{ r.start_time?.slice(0,5) }}</span>
              <span class="cal-event-name">{{ r.customer_name }}</span>
            </div>
            <div
              v-if="(calByDate[calDateStr(day)] || []).length > 4"
              class="cal-event-more"
              @click="openDayList(day)"
            >
              +{{ (calByDate[calDateStr(day)] || []).length - 4 }} 筆
            </div>
          </div>
        </div>
      </div></div>
    </template>

    <!-- 行事曆詳情浮層 -->
    <transition name="fade">
      <div v-if="detailR" class="cal-modal-overlay" @click.self="detailR = null">
        <div class="cal-modal">
          <button class="cal-modal-close" @click="detailR = null">✕</button>
          <div class="cal-modal-top">
            <span class="abr-status-tag" :class="`abr-status-tag--${detailR.status}`">{{ STATUS_LABELS[detailR.status] }}</span>
            <span class="abr-code">{{ detailR.reservation_code }}</span>
            <span class="abr-store-badge">{{ detailR.store_name }}</span>
          </div>
          <div class="cal-modal-body">
            <div><b>顧客：</b>{{ detailR.customer_name }} · {{ detailR.customer_phone }}</div>
            <div v-if="detailR.customer_email"><b>信箱：</b>{{ detailR.customer_email }}</div>
            <div><b>服務：</b>{{ detailR.service_name }}<span v-if="detailR.service_item_name"> · {{ detailR.service_item_name }}</span></div>
            <div><b>時段：</b>{{ detailR.slot_date }} {{ fmtServiceTime(detailR.start_time, detailR.duration_minutes, detailR.end_time) }}</div>
            <div v-if="detailR.notes"><b>備註：</b>{{ detailR.notes }}</div>
            <div><b>建立：</b>{{ fmtDatetime(detailR.created_at) }}</div>
          </div>
          <div class="cal-modal-actions">
            <template v-if="TRANSITIONS[detailR.status]?.length">
              <button v-for="next in TRANSITIONS[detailR.status]" :key="next"
                class="abr-btn abr-btn--sm"
                :class="next === 'cancelled' ? 'abr-btn--cancel' : 'abr-btn--confirm'"
                :disabled="updating[detailR.id]"
                @click="updateStatus(detailR, next)">
                <span v-if="updating[detailR.id]" class="abr-spinner abr-spinner--sm" />
                {{ ACTION_LABELS[next] }}
              </button>
            </template>
            <template v-else>
              <button class="abr-btn abr-btn--sm abr-btn--delete" :disabled="deleting[detailR.id]" @click="deleteReservation(detailR)">
                刪除記錄
              </button>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <!-- 當日清單浮層 -->
    <transition name="fade">
      <div v-if="dayListDate" class="cal-modal-overlay" @click.self="dayListDate = null">
        <div class="cal-modal cal-modal--day">
          <button class="cal-modal-close" @click="dayListDate = null">✕</button>
          <h3 class="cal-modal-day-title">{{ calDateStr(dayListDate) }} 預約清單</h3>
          <div class="cal-modal-scroll">
            <div v-for="r in (calByDate[calDateStr(dayListDate)] || [])" :key="r.id"
              class="cal-daylist-item" :class="`cal-daylist-item--${r.status}`" @click="openDetail(r); dayListDate = null">
              <div class="cal-daylist-left">
                <span class="cal-event-time">{{ r.start_time?.slice(0,5) }}</span>
                <span class="abr-status-tag abr-status-tag--sm" :class="`abr-status-tag--${r.status}`">{{ STATUS_LABELS[r.status] }}</span>
              </div>
              <div class="cal-daylist-info">
                <div class="cal-daylist-name">{{ r.customer_name }}</div>
                <div class="cal-daylist-svc">{{ r.service_name }}<span v-if="r.service_item_name"> · {{ r.service_item_name }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()

function handle401() {
  router.push({ name: 'AdminLogin' })
}

// ── 顯示模式 ─────────────────────────────────────────────
const viewMode = ref('calendar')

function setView(mode) {
  viewMode.value = mode
  if (mode === 'calendar') loadCal()
  else load()
}

// ── 篩選 ─────────────────────────────────────────────
const filterStore  = ref('')
const filterDate   = ref('')
const filterStatus = ref('')

// ── 資料 ─────────────────────────────────────────────
const stores       = ref([])
const reservations = ref([])
const loading      = ref(false)
const loadError    = ref('')

// ── 操作狀態 ─────────────────────────────────────────
const updating     = reactive({})
const updateErrors = reactive({})
const deleting     = reactive({})

// ── 行事曆狀態 ───────────────────────────────────────
const now = new Date()
const calYear      = ref(now.getFullYear())
const calMonthNum  = ref(now.getMonth() + 1)   // 1-12
const detailR      = ref(null)
const dayListDate  = ref(null)

const WEEK_DAYS = ['日', '一', '二', '三', '四', '五', '六']

// 當月天數
const calDaysInMonth = computed(() => {
  return new Date(calYear.value, calMonthNum.value, 0).getDate()
})

// 月第一天是星期幾（0=日...6=六）
const calFirstWeekday = computed(() => {
  return new Date(calYear.value, calMonthNum.value - 1, 1).getDay()
})

// 今天判斷
function isToday(day) {
  const t = new Date()
  return t.getFullYear() === calYear.value && t.getMonth() + 1 === calMonthNum.value && t.getDate() === day
}

// 產生 YYYY-MM-DD 字串
function calDateStr(day) {
  return `${calYear.value}-${String(calMonthNum.value).padStart(2,'0')}-${String(day).padStart(2,'0')}`
}

// 依日期分組
const calByDate = computed(() => {
  const map = {}
  for (const r of reservations.value) {
    if (!map[r.slot_date]) map[r.slot_date] = []
    map[r.slot_date].push(r)
  }
  return map
})

function calPrev() {
  if (calMonthNum.value === 1) { calYear.value--; calMonthNum.value = 12 }
  else calMonthNum.value--
  loadCal()
}
function calNext() {
  if (calMonthNum.value === 12) { calYear.value++; calMonthNum.value = 1 }
  else calMonthNum.value++
  loadCal()
}
function calToday() {
  const t = new Date()
  calYear.value = t.getFullYear()
  calMonthNum.value = t.getMonth() + 1
  loadCal()
}

async function loadCal() {
  const yr  = calYear.value
  const mo  = String(calMonthNum.value).padStart(2,'0')
  const days = new Date(yr, calMonthNum.value, 0).getDate()
  const date_from = `${yr}-${mo}-01`
  const date_to   = `${yr}-${mo}-${String(days).padStart(2,'0')}`

  loading.value      = true
  loadError.value    = ''
  reservations.value = []
  const params = { date_from, date_to }
  if (filterStore.value)  params.store_id = filterStore.value
  if (filterStatus.value) params.status   = filterStatus.value
  try {
    const res = await axios.get('/api/admin/booking/reservations', { params })
    reservations.value = res.data.reservations || []
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    loadError.value = e.response?.data?.message || '載入失敗，請重新整理'
  } finally {
    loading.value = false
  }
}

function openDetail(r) {
  detailR.value = r
}
function openDayList(day) {
  dayListDate.value = day
}

// ── 常數 ─────────────────────────────────────────────
const STATUS_LABELS = {
  pending_email: '待客人信箱確認',
  pending:       '待確認',
  confirmed:     '已確認',
  completed:     '已完成',
  cancelled:     '已取消',
}

const TRANSITIONS = {
  pending_email: ['cancelled'],
  pending:       ['confirmed', 'cancelled'],
  confirmed:     ['completed', 'cancelled'],
  completed:     [],
  cancelled:     [],
}

const ACTION_LABELS = {
  confirmed: '確認預約',
  completed: '標記完成',
  cancelled: '取消預約',
}

// ── 統計 ─────────────────────────────────────────────
const statusSummary = computed(() => {
  const counts = {}
  for (const r of reservations.value) {
    counts[r.status] = (counts[r.status] || 0) + 1
  }
  return counts
})

// ── 工具 ─────────────────────────────────────────────
function fmtServiceTime(startTime, durationMins, endTime) {
  if (!startTime) return ''
  const [h, m] = startTime.split(':').map(Number)
  const startStr = `${String(h % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  if (durationMins) {
    const endTotal = h * 60 + m + Number(durationMins)
    const eh = Math.floor(endTotal / 60) % 24
    const em = endTotal % 60
    return `${startStr}–${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`
  }
  if (endTime) return `${startStr}–${endTime.slice(0, 5)}`
  return startStr
}

function fmtDatetime(s) {
  if (!s) return ''
  const d = new Date(s)
  return d.toLocaleString('zh-TW', {
    timeZone: 'Asia/Taipei',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

function clearDate() {
  filterDate.value = ''
  load()
}

// ── 載入 (卡片模式) ───────────────────────────────────
async function loadStores() {
  try {
    const res = await axios.get('/api/admin/booking/stores')
    stores.value = (res.data.stores || []).filter(s => s.status === 'active')
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
  }
}

async function load() {
  loading.value      = true
  loadError.value    = ''
  reservations.value = []
  const params = {}
  if (filterStore.value)  params.store_id = filterStore.value
  if (filterDate.value)   params.date     = filterDate.value
  if (filterStatus.value) params.status   = filterStatus.value
  try {
    const res = await axios.get('/api/admin/booking/reservations', { params })
    reservations.value = res.data.reservations || []
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    loadError.value = e.response?.data?.message || '載入失敗，請重新整理'
  } finally {
    loading.value = false
  }
}

// ── 更新狀態 ─────────────────────────────────────────
async function updateStatus(r, newStatus) {
  updating[r.id]     = true
  updateErrors[r.id] = ''
  try {
    const res = await axios.put(`/api/admin/booking/reservations/${r.id}`, { status: newStatus })
    if (res.data.success) {
      r.status = newStatus
    } else {
      updateErrors[r.id] = res.data.message || '操作失敗'
    }
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    updateErrors[r.id] = e.response?.data?.message || '操作失敗'
  } finally {
    updating[r.id] = false
  }
}

// ── 刪除預約 ─────────────────────────────────────────
async function deleteReservation(r) {
  if (!confirm(`確定要刪除預約 ${r.reservation_code}？`)) return
  deleting[r.id] = true
  try {
    const res = await axios.delete(`/api/admin/booking/reservations/${r.id}`)
    if (res.data.success) {
      reservations.value = reservations.value.filter(x => x.id !== r.id)
      if (detailR.value?.id === r.id) detailR.value = null
    } else {
      alert(res.data.message || '刪除失敗')
    }
  } catch (e) {
    if (e.response?.status === 401) { handle401(); return }
    alert(e.response?.data?.message || '刪除失敗')
  } finally {
    deleting[r.id] = false
  }
}

onMounted(async () => {
  await loadStores()
  await loadCal()
})
</script>

<style scoped>
.abr-page {
  padding: 24px;
  max-width: 1100px;
}
.abr-header-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 20px;
}
.abr-title {
  font-size: 1.4rem; font-weight: 700; color: #2c3e50; margin: 0;
}

/* 切換分頁 */
.abr-view-tabs {
  display: flex; border: 1px solid #dce1e7; border-radius: 8px; overflow: hidden;
}
.abr-view-tab {
  padding: 6px 18px; background: #fff; border: none; cursor: pointer;
  font-size: 0.88rem; font-weight: 500; color: #7f8c8d;
  transition: background .15s, color .15s;
}
.abr-view-tab--on { background: #2c3e50; color: #fff; }

/* 篩選列 */
.abr-filters {
  display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end;
  background: #fff; border: 1px solid #e8ecf0; border-radius: 10px;
  padding: 16px 20px; margin-bottom: 16px;
}
.abr-filter-group { display: flex; flex-direction: column; gap: 4px; }
.abr-filter-group label { font-size: 0.8rem; color: #7f8c8d; font-weight: 500; }

/* 行事曆導覽列 */
.abr-cal-nav {
  display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
  justify-content: space-between;
  background: #fff; border: 1px solid #e8ecf0; border-radius: 10px;
  padding: 12px 16px; margin-bottom: 14px;
}
.abr-cal-nav-left { display: flex; align-items: center; gap: 8px; }
.abr-cal-nav-right { display: flex; align-items: center; gap: 8px; }
.abr-cal-title { font-size: 1.05rem; font-weight: 700; color: #2c3e50; min-width: 120px; text-align: center; }

/* 統計列 */
.abr-summary { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; align-items: center; }
.abr-summary-chip { padding: 3px 10px; border-radius: 20px; font-size: 0.78rem; font-weight: 600; }
.abr-summary-chip--pending   { background: #fef9e7; color: #d68910; border: 1px solid #f9e79f; }
.abr-summary-chip--confirmed { background: #eafaf1; color: #1e8449; border: 1px solid #a9dfbf; }
.abr-summary-chip--completed { background: #f2f3f4; color: #5d6d7e; border: 1px solid #d5d8dc; }
.abr-summary-chip--cancelled { background: #fdedec; color: #c0392b; border: 1px solid #f5b7b1; }
.abr-summary-chip--total     { background: #f4f6f9; color: #2c3e50; border: 1px solid #dce1e7; }

/* 狀態佔位 */
.abr-state { display: flex; align-items: center; gap: 10px; color: #7f8c8d; padding: 40px 0; font-size: 0.95rem; }
.abr-alert--error { background: #fdedec; border: 1px solid #f5b7b1; border-radius: 8px; padding: 12px 16px; color: #c0392b; font-size: 0.9rem; }
.abr-empty { text-align: center; color: #95a5a6; padding: 60px 0; font-size: 0.95rem; }

/* ─── 行事曆格線 ─────────────────────────────────────── */
.cal-grid-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #dce1e7;
  border-radius: 10px;
}
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(100px, 1fr));
  min-width: 700px;
  background: #e8ecf0;
  gap: 1px;
}
.cal-weekday {
  background: #2c3e50; color: #fff;
  text-align: center; padding: 8px 0;
  font-size: 0.82rem; font-weight: 700;
}
.cal-day {
  background: #fff;
  min-height: 110px;
  padding: 6px;
  display: flex; flex-direction: column; gap: 3px;
  position: relative;
}
.cal-day--blank { background: #f8f9fa; }
.cal-day--today .cal-day-num {
  background: #2c3e50; color: #fff;
  border-radius: 50%; width: 24px; height: 24px;
  display: flex; align-items: center; justify-content: center;
}
.cal-day-num {
  font-size: 0.82rem; font-weight: 600; color: #5d6d7e;
  width: 24px; height: 24px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 2px; flex-shrink: 0;
}
.cal-events { display: flex; flex-direction: column; gap: 2px; flex: 1; overflow: hidden; }

/* 預約小方塊 */
.cal-event {
  display: flex; align-items: center; gap: 3px;
  border-radius: 4px; padding: 2px 5px;
  font-size: 0.72rem; cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  transition: opacity .15s;
}
.cal-event:hover { opacity: 0.82; }
.cal-event--pending_email { background: #d6eaf8; color: #1a6fa8; }
.cal-event--pending       { background: #fef9e7; color: #b7770d; border: 1px solid #f9e79f; }
.cal-event--confirmed     { background: #eafaf1; color: #1e8449; border: 1px solid #a9dfbf; }
.cal-event--completed     { background: #f2f3f4; color: #5d6d7e; border: 1px solid #d5d8dc; }
.cal-event--cancelled     { background: #fdedec; color: #c0392b; border: 1px solid #f5b7b1; text-decoration: line-through; }
.cal-event-time { font-weight: 700; flex-shrink: 0; }
.cal-event-name { overflow: hidden; text-overflow: ellipsis; }

.cal-event-more {
  font-size: 0.68rem; color: #2c3e50; cursor: pointer;
  padding: 1px 5px; border-radius: 3px; background: #f4f6f9;
  text-align: center; font-weight: 600;
}
.cal-event-more:hover { background: #dce1e7; }

/* ─── 浮層 (詳情 / 日清單) ──────────────────────────── */
.cal-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.38);
  z-index: 2000; display: flex; align-items: center; justify-content: center;
}
.cal-modal {
  background: #fff; border-radius: 12px;
  padding: 24px 28px; min-width: 320px; max-width: 480px; width: 90%;
  box-shadow: 0 6px 30px rgba(0,0,0,0.2); position: relative;
}
.cal-modal--day { max-width: 400px; }
.cal-modal-close {
  position: absolute; top: 12px; right: 14px;
  background: none; border: none; font-size: 1.1rem;
  color: #7f8c8d; cursor: pointer; line-height: 1;
}
.cal-modal-close:hover { color: #2c3e50; }
.cal-modal-top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; }
.cal-modal-body { display: flex; flex-direction: column; gap: 6px; font-size: 0.9rem; color: #444; margin-bottom: 16px; }
.cal-modal-body b { color: #2c3e50; }
.cal-modal-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.cal-modal-day-title { font-size: 1rem; font-weight: 700; color: #2c3e50; margin: 0 0 14px; }
.cal-modal-scroll { display: flex; flex-direction: column; gap: 8px; max-height: 380px; overflow-y: auto; }
.cal-daylist-item {
  display: flex; align-items: flex-start; gap: 10px;
  border: 1px solid #e8ecf0; border-radius: 8px; padding: 10px 12px;
  cursor: pointer; transition: background .15s;
}
.cal-daylist-item:hover { background: #f8f9fa; }
.cal-daylist-left { display: flex; flex-direction: column; align-items: center; gap: 4px; flex-shrink: 0; }
.cal-daylist-info { flex: 1; }
.cal-daylist-name { font-weight: 600; font-size: 0.9rem; color: #2c3e50; }
.cal-daylist-svc  { font-size: 0.8rem; color: #7f8c8d; margin-top: 2px; }

.abr-status-tag--sm { font-size: 0.65rem; padding: 1px 6px; }

/* 卡片列表 */
.abr-list { display: flex; flex-direction: column; gap: 10px; }
.abr-card { display: flex; background: #fff; border: 1px solid #e8ecf0; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.abr-card__stripe { width: 5px; flex-shrink: 0; }
.abr-card--pending_email .abr-card__stripe { background: #3498db; }
.abr-card--pending   .abr-card__stripe { background: #f39c12; }
.abr-card--confirmed .abr-card__stripe { background: #27ae60; }
.abr-card--completed .abr-card__stripe { background: #95a5a6; }
.abr-card--cancelled .abr-card__stripe { background: #e74c3c; }
.abr-card__body { flex: 1; padding: 14px 18px; display: flex; flex-direction: column; gap: 7px; }
.abr-card__top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.abr-code { font-family: monospace; font-size: 0.9rem; font-weight: 700; color: #2c3e50; background: #f4f6f9; padding: 2px 8px; border-radius: 5px; }
.abr-store-badge { background: #eaf0fb; color: #1a5276; padding: 2px 9px; border-radius: 4px; font-size: 0.78rem; font-weight: 500; }
.abr-status-tag { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; }
.abr-status-tag--pending_email { background: #eaf4fb; color: #1a6fa8; border: 1px solid #aed6f1; }
.abr-status-tag--pending   { background: #fef9e7; color: #d68910; border: 1px solid #f9e79f; }
.abr-status-tag--confirmed { background: #eafaf1; color: #1e8449; border: 1px solid #a9dfbf; }
.abr-status-tag--completed { background: #f2f3f4; color: #5d6d7e; border: 1px solid #d5d8dc; }
.abr-status-tag--cancelled { background: #fdedec; color: #c0392b; border: 1px solid #f5b7b1; }
.abr-card__meta { font-size: 0.87rem; color: #555; display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }
.abr-sep { color: #bdc3c7; }
.abr-muted { color: #7f8c8d; }
.abr-method-badge { font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 20px; flex-shrink: 0; }
.abr-method-badge--line  { background: #e8f5e9; color: #2e7d32; }
.abr-method-badge--email { background: #e3f2fd; color: #1565c0; }
.abr-card__notes { font-size: 0.82rem; color: #7f8c8d; font-style: italic; }
.abr-card__created { font-size: 0.78rem; color: #bdc3c7; }
.abr-card__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 4px; }
.abr-action-err { font-size: 0.78rem; color: #e74c3c; }

/* 按鈕 */
.abr-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 9px 16px; border-radius: 7px; border: none; font-size: 0.9rem; font-weight: 500; cursor: pointer; transition: background 0.15s, opacity 0.15s; white-space: nowrap; }
.abr-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.abr-btn--sm      { padding: 6px 12px; font-size: 0.82rem; }
.abr-btn--primary { background: #2c3e50; color: #fff; }
.abr-btn--primary:hover:not(:disabled) { background: #34495e; }
.abr-btn--ghost   { background: #fff; color: #2c3e50; border: 1px solid #dce1e7; }
.abr-btn--ghost:hover:not(:disabled)   { background: #f4f6f9; }
.abr-btn--confirm { background: #27ae60; color: #fff; }
.abr-btn--confirm:hover:not(:disabled) { background: #219a52; }
.abr-btn--cancel  { background: #fff; color: #e74c3c; border: 1px solid #e74c3c; }
.abr-btn--cancel:hover:not(:disabled)  { background: #fdedec; }
.abr-btn--delete  { background: #fff; color: #7f8c8d; border: 1px solid #bdc3c7; }
.abr-btn--delete:hover:not(:disabled)  { background: #f4f6f9; }

.abr-terminal { font-size: 0.78rem; font-weight: 600; padding: 2px 10px; border-radius: 20px; border: 1px solid; }
.abr-terminal--completed     { background: #f2f3f4; color: #5d6d7e; border-color: #d5d8dc; }
.abr-terminal--cancelled     { background: #fdedec; color: #c0392b; border-color: #f5b7b1; }
.abr-terminal--pending_email { background: #eaf4fb; color: #1a6fa8; border-color: #aed6f1; }

.abr-input { padding: 8px 10px; border: 1px solid #dce1e7; border-radius: 7px; font-size: 0.9rem; background: #fff; min-width: 120px; -webkit-appearance: none; }
.abr-input:focus { outline: none; border-color: #2c3e50; box-shadow: 0 0 0 3px rgba(44,62,80,0.07); }

.abr-spinner { display: inline-block; width: 28px; height: 28px; border: 3px solid rgba(0,0,0,0.12); border-top-color: #7f8c8d; border-radius: 50%; animation: spin 0.7s linear infinite; }
.abr-spinner--sm    { width: 14px; height: 14px; border-width: 2px; }
.abr-spinner--lg    { width: 22px; height: 22px; }
.abr-spinner--white { border-color: rgba(255,255,255,0.3); border-top-color: #fff; }
@keyframes spin { to { transform: rotate(360deg); } }

.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@media (max-width: 600px) {
  .abr-page    { padding: 14px; }
  .abr-filters { padding: 12px 14px; }
  .abr-input   { min-width: 100px; }
  .cal-day { min-height: 80px; padding: 4px; }
  .cal-event { font-size: 0.65rem; }
  .abr-cal-nav { gap: 8px; }
  .abr-cal-nav-right { flex-wrap: wrap; }
}
</style>
